import { useState, useRef, useEffect, FC, ChangeEvent } from 'react'
import { Icon } from '../Icon'
import { colors } from '../colors'
import {
  SelectContainer,
  SelectButton,
  SelectDropdown,
  SelectOption,
  SelectOptionText,
  SelectOptionCheck,
  SelectOptionsList,
  SelectSearchWrapper,
  SelectSearchInput,
  SelectEmptyState,
  SelectSearchIcon,
  SelectLoader,
  SelectLoadingState
} from './Select.styles.ts'

export interface SelectOption {
  value: string
  label: string
}

interface BaseSelectProps {
  options: SelectOption[]
  placeholder?: string
  className?: string
  disabled?: boolean
  searchValue?: string
  searchPlaceholder?: string
  onSearchChange?: (value: string) => void
  isMulti?: boolean
  isLoading?: boolean
}

interface SingleSelectProps extends BaseSelectProps {
  isMulti?: false
  value: string
  onChange: (value: string) => void
}

interface MultiSelectProps extends BaseSelectProps {
  isMulti: true
  value: string[]
  onChange: (value: string[]) => void
}

type SelectProps = SingleSelectProps | MultiSelectProps

export const Select: FC<SelectProps> = props => {
  const {
    options,
    value,
    onChange,
    placeholder = 'Выберите...',
    className,
    disabled,
    searchValue = '',
    searchPlaceholder = 'Поиск...',
    onSearchChange,
    isMulti,
    isLoading = false
  } = props

  const [isOpen, setIsOpen] = useState(false)
  const selectRef = useRef<HTMLDivElement>(null)
  const isMultiMode = !!isMulti

  const selectedValues = isMultiMode
    ? Array.isArray(value)
      ? value
      : []
    : value
      ? [value as string]
      : []

  const selectedValuesSet = new Set(selectedValues)
  const selectedLabels = options
    .filter(option => selectedValuesSet.has(option.value))
    .map(option => option.label)

  const buttonText =
    selectedLabels.length > 0
      ? isMultiMode
        ? selectedLabels.join(', ')
        : selectedLabels[0]
      : isMultiMode && selectedValues.length > 0
        ? `Выбрано ${selectedValues.length}`
        : placeholder

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleOptionClick = (optionValue: string) => {
    if (isMultiMode) {
      const currentValues = Array.isArray(value) ? value : []
      const nextValues = currentValues.includes(optionValue)
        ? currentValues.filter(v => v !== optionValue)
        : [...currentValues, optionValue]
      ;(onChange as (val: string[]) => void)(nextValues)
      return
    }

    ;(onChange as (val: string) => void)(optionValue)
    setIsOpen(false)
  }

  const handleButtonClick = () => {
    setIsOpen(prev => !prev)
  }

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!onSearchChange) {
      return
    }
    onSearchChange(event.target.value)
  }

  const hasOptions = options.length > 0

  return (
    <SelectContainer ref={selectRef} className={className}>
      <SelectButton onClick={handleButtonClick} disabled={disabled}>
        <span>{buttonText}</span>
        <Icon type="chevron_down" size={12} color={colors.gray600} />
      </SelectButton>

      {isOpen && (
        <SelectDropdown>
          {!!onSearchChange && (
            <SelectSearchWrapper>
              <SelectSearchIcon>
                <Icon type="search" size={16} color={colors.gray400} />
              </SelectSearchIcon>
              <SelectSearchInput
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={handleSearchChange}
                autoFocus
              />
            </SelectSearchWrapper>
          )}
          <SelectOptionsList>
            {isLoading ? (
              <SelectLoadingState>
                <SelectLoader aria-label="Загрузка" />
                <span>Загрузка...</span>
              </SelectLoadingState>
            ) : hasOptions ? (
              options.map(option => (
                <SelectOption
                  key={option.value}
                  onClick={() => handleOptionClick(option.value)}
                  isSelected={selectedValuesSet.has(option.value)}
                >
                  <SelectOptionText>{option.label}</SelectOptionText>
                  {selectedValuesSet.has(option.value) && (
                    <SelectOptionCheck>
                      <Icon type="check" size={14} color={colors.orange500} />
                    </SelectOptionCheck>
                  )}
                </SelectOption>
              ))
            ) : (
              <SelectEmptyState>Ничего не найдено</SelectEmptyState>
            )}
          </SelectOptionsList>
        </SelectDropdown>
      )}
    </SelectContainer>
  )
}
